import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, {
  formatApiError, tVulnType, VULN_TYPES, VULN_TYPE_KEYS,
  SEVERITY, SEVERITY_KEYS, VULN_STATUS, VULN_STATUS_KEYS,
  tThreatCategory, tConfidence,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import ControlsPanel from "@/components/shared/ControlsPanel";
import ScenariosPanel from "@/components/shared/ScenariosPanel";
import { SevBadge, VulnStatusBadge, CritBadge, ThreatStatusBadge } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft, ShieldAlert, Server, Boxes, Info, Link2, Plus, X, Archive, Pencil, GitBranch, Loader2, Crosshair,
} from "lucide-react";

function Field({ label, children, mono }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</dt>
      <dd className={`mt-1 text-sm ${mono ? "font-mono" : ""}`}>{children ?? <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid, action }) {
  return (
    <section className="border border-border bg-card rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <h2 className="flex items-center gap-2 font-head font-semibold">{Icon && <Icon className="h-4 w-4 text-primary" />} {title}</h2>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

export default function VulnerabilityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const qc = useQueryClient();
  const writable = can("vulnerability:write");
  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [assetChoice, setAssetChoice] = useState("");
  const [form, setForm] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["vulnerability", id],
    queryFn: async () => (await api.get(`/vulnerabilities/${id}`)).data,
  });
  const { data: exposure } = useQuery({
    queryKey: ["vulnerability-exposure", id],
    queryFn: async () => (await api.get(`/vulnerabilities/${id}/service-exposure`)).data,
    enabled: !!data,
  });
  const { data: allAssets = [] } = useQuery({
    queryKey: ["assets-min"],
    queryFn: async () => (await api.get("/assets")).data,
    enabled: linkOpen,
  });
  const { data: threats = [] } = useQuery({
    queryKey: ["vulnerability-threats", id],
    queryFn: async () => (await api.get(`/vulnerabilities/${id}/threats`)).data,
    enabled: !!data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["vulnerability", id] });
    qc.invalidateQueries({ queryKey: ["vulnerability-exposure", id] });
    qc.invalidateQueries({ queryKey: ["vulnerabilities"] });
  };

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/vulnerabilities/${id}`, form)).data,
    onSuccess: () => { toast.success("Vulnérabilité mise à jour"); setEditOpen(false); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const archive = useMutation({
    mutationFn: async () => (await api.delete(`/vulnerabilities/${id}`)).data,
    onSuccess: (r) => { toast.success(`Vulnérabilité archivée (statut conservé : ${r.status})`); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const link = useMutation({
    mutationFn: async (assetId) => api.post(`/vulnerabilities/${id}/assets/${assetId}`),
    onSuccess: () => { toast.success("Actif associé"); setLinkOpen(false); setAssetChoice(""); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const unlink = useMutation({
    mutationFn: async (assetId) => api.delete(`/vulnerabilities/${id}/assets/${assetId}`),
    onSuccess: () => { toast.success("Association retirée"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading)
    return <AppShell title="Vulnérabilité"><div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div></AppShell>;
  if (isError || !data)
    return <AppShell title="Vulnérabilité"><p className="text-sm text-muted-foreground">Vulnérabilité introuvable.</p><Button variant="outline" className="mt-4 rounded-sm gap-2" onClick={() => navigate("/vulnerabilities")}><ArrowLeft className="h-4 w-4" /> Retour</Button></AppShell>;

  const v = data;
  const cvss = v.cvss || {};
  const epss = v.epss || {};
  const linkedIds = new Set((v.assets || []).map((a) => a.id));
  const linkable = allAssets.filter((a) => !linkedIds.has(a.id));

  const openEdit = () => {
    setForm({
      title: v.title, description: v.description || "", vulnerability_type: v.vulnerability_type,
      severity: v.severity, status: v.status, source: v.source || "",
      source_reference: v.source_reference || "", cve_id: v.cve_id || "",
    });
    setEditOpen(true);
  };

  return (
    <AppShell title={v.title} subtitle="Fiche vulnérabilité — objet cyber dédié (Lot 1)"
      actions={
        <div className="flex gap-2">
          {writable && <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={openEdit} data-testid="edit-vulnerability-button"><Pencil className="h-4 w-4" /> Modifier</Button>}
          {writable && !v.archived_at && <Button variant="outline" size="sm" className="rounded-sm gap-2 text-orange-400" onClick={() => { if (window.confirm("Archiver cette vulnérabilité ? Le statut métier est conservé.")) archive.mutate(); }} data-testid="archive-vulnerability-button"><Archive className="h-4 w-4" /> Archiver</Button>}
          <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => navigate("/vulnerabilities")} data-testid="back-to-vulnerabilities"><ArrowLeft className="h-4 w-4" /> Vulnérabilités</Button>
        </div>
      }>
      <div className="space-y-6" data-testid="vulnerability-detail">
        {v.archived_at && (
          <div className="border border-orange-500/30 bg-orange-500/10 text-orange-300 rounded-sm px-4 py-2 text-sm" data-testid="archived-banner">
            Archivée le {new Date(v.archived_at).toLocaleString("fr-FR")} — statut métier conservé : {VULN_STATUS[v.status]?.label || v.status}.
          </div>
        )}

        <Section title="Identité" icon={Info} testid="section-vuln-identity">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Field label="Identifiant CROS" mono>{v.id}</Field>
            <Field label="Type">{tVulnType(v.vulnerability_type)}</Field>
            <Field label="Gravité"><SevBadge value={v.severity} /></Field>
            <Field label="Statut"><VulnStatusBadge value={v.status} /></Field>
            <Field label="Source">{v.source || null}</Field>
            <Field label="Référence source" mono>{v.source_reference || null}</Field>
            <Field label="Première observation" mono>{v.first_observed_at || null}</Field>
            <Field label="Dernière observation" mono>{v.last_observed_at || null}</Field>
            <div className="col-span-2 md:col-span-4"><Field label="Description">{v.description || null}</Field></div>
          </dl>
        </Section>

        <Section title="Références externes" icon={ShieldAlert} testid="section-vuln-external">
          <p className="text-xs text-muted-foreground mb-4">Les références externes sont optionnelles. Le CVE n'est jamais l'identité métier — l'identifiant CROS l'est.</p>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Field label="CVE" mono>{v.cve_id || null}</Field>
            <Field label="CWE" mono>{(v.cwe_ids || []).join(", ") || null}</Field>
            <Field label="CVSS (score de base)" mono>{cvss.base_score != null ? `${cvss.base_score} (${cvss.version || "?"})` : null}</Field>
            <Field label="EPSS" mono>{epss.score != null ? `${(epss.score * 100).toFixed(1)}% (pct ${epss.percentile != null ? (epss.percentile * 100).toFixed(0) + "%" : "?"})` : null}</Field>
            <div className="col-span-2 md:col-span-4"><Field label="Vecteur CVSS" mono>{cvss.vector || null}</Field></div>
          </dl>
        </Section>

        <Section title={`Actifs affectés — ${(v.assets || []).length}`} icon={Server} testid="section-vuln-assets"
          action={writable && <Button size="sm" className="rounded-sm gap-2" onClick={() => setLinkOpen(true)} data-testid="link-asset-button"><Plus className="h-4 w-4" /> Associer un actif</Button>}>
          <p className="text-xs text-muted-foreground mb-4">Relation many-to-many <span className="font-mono">VULNERABILITY —AFFECTS→ ASSET</span>. Une vulnérabilité peut affecter plusieurs actifs.</p>
          {(v.assets || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun actif associé.</p>
          ) : (
            <div className="border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead>Actif</TableHead>
                    <TableHead>Criticité</TableHead>
                    <TableHead>Source de détection</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {v.assets.map((a) => (
                    <TableRow key={a.id} className="border-border" data-testid={`vuln-asset-row-${a.id}`}>
                      <TableCell className="font-medium">
                        <button className="flex items-center gap-2 hover:text-primary" onClick={() => navigate(`/assets/${a.id}`)} data-testid={`vuln-asset-nav-${a.id}`}>
                          <Server className="h-3.5 w-3.5 text-muted-foreground" /> {a.name}
                        </button>
                      </TableCell>
                      <TableCell>{a.criticality ? <CritBadge value={a.criticality} /> : "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{a.link?.detection_source || "—"}</TableCell>
                      <TableCell className="text-right">
                        {writable && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" title="Retirer l'association" onClick={() => { if (window.confirm("Retirer cette association ?")) unlink.mutate(a.id); }} data-testid={`unlink-asset-${a.id}`}><X className="h-4 w-4" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Section>

        <Section title={`Menaces associées — ${threats.length}`} icon={Crosshair} testid="section-vuln-threats">
          <p className="text-xs text-muted-foreground mb-4">Menaces explicitement associées (lecture seule). La gestion des associations se fait depuis la fiche Menace. Une association ne crée aucun risque.</p>
          {threats.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune menace associée.</p>
          ) : (
            <div className="border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead>Menace</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Gravité</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Confiance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {threats.map((t) => (
                    <TableRow key={t.id} className="border-border" data-testid={`vuln-threat-row-${t.id}`}>
                      <TableCell className="font-medium">
                        <button className="flex items-center gap-2 hover:text-primary" onClick={() => navigate(`/threats/${t.id}`)} data-testid={`vuln-threat-nav-${t.id}`}>
                          <Crosshair className="h-3.5 w-3.5 text-muted-foreground" /> {t.name}
                        </button>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">{tThreatCategory(t.category)}</TableCell>
                      <TableCell><SevBadge value={t.severity} /></TableCell>
                      <TableCell><ThreatStatusBadge value={t.status} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{tConfidence(t.link?.confidence)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Section>

        <ControlsPanel endpoint={`/vulnerabilities/${id}/controls`} queryKey={["vuln-controls", id]} />

        <ScenariosPanel endpoint={`/vulnerabilities/${id}/scenarios`} queryKey={["vuln-scenarios", id]} />

        <Section title={`Exposition de service (dérivée) — ${(exposure?.exposures || []).length}`} icon={GitBranch} testid="section-vuln-exposure">
          <p className="text-xs text-muted-foreground mb-4">
            Exposition DÉRIVÉE du graphe structurel v0.1 (Vulnerability → Actif affecté → dépendances → Service).
            <span className="text-foreground"> Aucun score, aucun risque, aucune menace.</span>
          </p>
          {!exposure ? (
            <p className="text-sm text-muted-foreground font-mono">Calcul…</p>
          ) : (exposure.exposures || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun service structurellement exposé pour les actifs affectés.</p>
          ) : (
            <div className="space-y-3">
              {exposure.exposures.map((ex, i) => (
                <div key={ex.service_id} className="border border-border rounded-sm p-4" data-testid={`exposure-row-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Boxes className="h-4 w-4 text-primary" />
                    <button className="font-medium hover:text-primary" onClick={() => navigate(`/services/${ex.service_id}`)} data-testid={`exposure-service-nav-${i}`}>{ex.service_name}</button>
                    <span className="text-muted-foreground text-xs">via</span>
                    <Badge variant="outline" className="rounded-sm font-mono text-xs">{ex.asset_name}</Badge>
                    {(ex.relations_traversed || []).map((r) => (
                      <Badge key={`${ex.service_id}-${r}`} variant="outline" className="rounded-sm font-mono text-[10px] text-muted-foreground">{r}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 leading-snug">{ex.reason}</p>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="vulnerability-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Modifier la vulnérabilité</DialogTitle>
            <DialogDescription>La gravité reste intrinsèque (jamais un risque). Archiver ≠ fermer.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label className="text-xs">Titre</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" data-testid="edit-vuln-title-input" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.vulnerability_type} onValueChange={(x) => setForm({ ...form, vulnerability_type: x })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-vuln-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{VULN_TYPE_KEYS.map((k) => <SelectItem key={k} value={k}>{VULN_TYPES[k]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Gravité</Label>
                <Select value={form.severity} onValueChange={(x) => setForm({ ...form, severity: x })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-vuln-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITY_KEYS.map((k) => <SelectItem key={k} value={k}>{SEVERITY[k].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Statut</Label>
                <Select value={form.status} onValueChange={(x) => setForm({ ...form, status: x })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-vuln-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{VULN_STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{VULN_STATUS[k].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">CVE (optionnel)</Label>
                <Input value={form.cve_id} onChange={(e) => setForm({ ...form, cve_id: e.target.value })} className="mt-1 rounded-sm font-mono" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => save.mutate()} disabled={!form?.title || save.isPending} data-testid="save-vuln-edit-button">
              {save.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link asset dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="link-asset-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Associer un actif</DialogTitle>
            <DialogDescription>Relation VULNERABILITY —AFFECTS→ ASSET.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs">Actif</Label>
            <Select value={assetChoice} onValueChange={setAssetChoice}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="link-asset-select"><SelectValue placeholder="Choisir un actif…" /></SelectTrigger>
              <SelectContent>
                {linkable.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setLinkOpen(false)}>Annuler</Button>
            <Button className="rounded-sm gap-2" onClick={() => link.mutate(assetChoice)} disabled={!assetChoice || link.isPending} data-testid="confirm-link-asset-button">
              <Link2 className="h-4 w-4" /> Associer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
